import { useCallback, useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useParams } from "react-router";

import { AssessmentDetailViewParams } from "@/features/assessments/features/answers/types";
import {
  AvailableTasksSectionRead,
  AvailableTasksThemeRead,
  TagsType,
  api,
  useApiV1AssessmentsSiteAssessmentsAvailableStandardsListQuery,
  useApiV1AssessmentsSiteAssessmentsAvailableTasksListQuery,
  useApiV1AssessmentsSiteAssessmentsAvailableTasksStandardListQuery,
  usePrefetch,
} from "@/services/api";
import { useAppDispatch } from "@/services/store";
import { AssessmentMode, Filters, Menu, MenuTask } from "./types";
import { NO_STANDARD_ID, adaptAvailableTasksSectionReadIntoMenu, adaptAvailableTasksThemeReadIntoMenu } from "./utils";

export const getTasksFromGroups = (groups?: Menu[]): MenuTask[] =>
  groups?.flatMap(group => group.sub_groups.flatMap(sub_group => sub_group.tasks)) ?? [];

export const getTasksFromThemes = (themes?: AvailableTasksThemeRead[]): MenuTask[] =>
  themes?.flatMap(theme => theme.sub_themes.flatMap(sub_theme => sub_theme.tasks)) ?? [];

export const getTasksFromSections = (sections?: AvailableTasksSectionRead[]): MenuTask[] =>
  sections?.flatMap(section => section.sub_sections.flatMap(subsection => subsection.tasks)) ?? [];

export interface FilterAndMode {
  filters: Filters;
  mode: AssessmentMode;
}

const getStatus = ({ filters, mode }: FilterAndMode) => {
  const fields = [
    ["unassigned", filters.unassigned],
    ["assigned_" + mode, filters.assigned],
    ["declined", filters.declined],
    ["in_review", filters.inReview],
    ["reviewed", filters.reviewed],
    ["is_answered", filters.isAnswered],
    ["is_notanswered", filters.isNotAnswered],
  ]
    .filter(([, value]) => value)
    .map(([key]) => key);

  return fields.length > 0 ? fields.join(",") : undefined;
};

export const useGetData = (selectedStandard: string | undefined, filtersAndMode?: FilterAndMode) => {
  const { siteAssessmentId } = useParams<AssessmentDetailViewParams>() as AssessmentDetailViewParams;
  const { showBoundary } = useErrorBoundary();
  const dispatch = useAppDispatch();

  const {
    data: standards,
    isLoading: standardsAreLoading,
    error: standardsError,
  } = useApiV1AssessmentsSiteAssessmentsAvailableStandardsListQuery(
    {
      siteAssessmentId: siteAssessmentId!,
    },
    { skip: !siteAssessmentId },
  );

  const status = filtersAndMode ? getStatus(filtersAndMode) : undefined;
  const {
    data: sections,
    isLoading: sectionsAreLoading,
    error: sectionsError,
  } = useApiV1AssessmentsSiteAssessmentsAvailableTasksStandardListQuery(
    {
      siteAssessmentId: siteAssessmentId!,
      standardId: +selectedStandard!,
      status,
    },
    { skip: !siteAssessmentId || selectedStandard === NO_STANDARD_ID || !selectedStandard },
  );

  const {
    data: themes,
    isLoading: themesAreLoading,
    error: themesError,
  } = useApiV1AssessmentsSiteAssessmentsAvailableTasksListQuery(
    {
      siteAssessmentId: siteAssessmentId!,
      status,
    },
    { skip: !siteAssessmentId },
  );

  const errors = [themesError, standardsError, sectionsError].filter(e => !!e);

  if (errors.length > 0) {
    showBoundary({
      errors,
      refetch: () => {
        const tags = [(themesError || standardsError || sectionsError) && "Tasks"].filter(
          tag => tag !== undefined,
        ) as TagsType[];
        dispatch(api.util.invalidateTags(tags));
      },
    });
  }

  const menulist = useMemo(() => {
    const items =
      selectedStandard !== NO_STANDARD_ID
        ? sections?.map(adaptAvailableTasksSectionReadIntoMenu)
        : themes?.map(adaptAvailableTasksThemeReadIntoMenu);
    return items ?? [];
  }, [selectedStandard, sections, themes]);

  const tasks: MenuTask[] = useMemo(() => getTasksFromGroups(menulist), [menulist]);

  return {
    tasks,
    themes,
    sections,
    menulist,
    standards,
    isLoading: themesAreLoading || standardsAreLoading || sectionsAreLoading,
  };
};

export const usePrefetchCallbacks = (assessmentId: string) => {
  const prefetchAssessments = usePrefetch("apiV1AssessmentsAssessmentsRetrieve");
  const prefetchStandards = usePrefetch("apiV1StandardsStandardsList");

  const prefetchTaskCallback = useCallback(() => {
    prefetchAssessments({ assessmentId: assessmentId });
    prefetchStandards({ ordering: "name" });
  }, []);

  return {
    prefetchTaskCallback,
  };
};
